import { useEffect, useMemo } from 'react';
import { Modal, Form, Select, Tag, Alert, Button, message } from '../../ui';
import type { TagTone } from '../../ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useConfirmedMutation } from '../../lib/confirm';
import { request } from '../../api/client';

interface Props { open: boolean; onClose: () => void; }

const LEVEL_COLOR: Record<string, TagTone> = { zone: 'purple', state: 'blue', district: 'cyan', beat: 'green' };

/**
 * Merge source territories INTO a target (same level). All customers move to the target,
 * the sources' SP bindings fold onto it, and the (childless) sources are deactivated.
 */
export function MergeTerritoriesModal({ open, onClose }: Props) {
  const [form] = Form.useForm();
  const qc = useQueryClient();
  const targetId = Form.useWatch('targetId', form);

  const { data: territories } = useQuery({
    queryKey: ['territories-list'],
    queryFn: () => request<any[]>({ url: '/territories' }),
    enabled: open,
  });
  const active = useMemo(() => (territories ?? []).filter((t: any) => t.isActive), [territories]);
  const targetLevel = active.find((t: any) => String(t.id) === String(targetId))?.level;
  const opt = (t: any) => ({ value: String(t.id), label: `${t.code} · ${t.name}` });

  useEffect(() => { if (open) form.resetFields(); }, [open, form]);

  const merge = useConfirmedMutation({ title: "Merge the selected territories?", content: "Their customers and coverage move into the target territory, and the sources are retired. This can’t be undone.", okText: "Yes, merge", danger: true }, {
    mutationFn: (v: any) => request({ url: '/territory-assignments/merge', method: 'POST', data: { targetId: v.targetId, sourceIds: v.sourceIds } }),
    onSuccess: (res: any) => {
      message.success(`Merged ${res?.merged ?? 0} territor(ies) — ${res?.movedRetailers ?? 0} retailer(s), ${res?.movedFarmers ?? 0} farmer(s) moved`);
      qc.invalidateQueries({ queryKey: ['territories-tree'] });
      qc.invalidateQueries({ queryKey: ['territories-list'] });
      qc.invalidateQueries({ queryKey: ['territory-coverage'] });
      qc.invalidateQueries({ queryKey: ['retailers'] });
      qc.invalidateQueries({ queryKey: ['farmers'] });
      qc.invalidateQueries({ queryKey: ['badges'] });
      onClose();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to merge'),
  });

  return (
    <Modal open={open} onOpenChange={(o) => { if (!o) onClose(); }} title="Merge territories"
      footer={<><Button variant="default" onClick={onClose}>Cancel</Button><Button variant="primary" loading={merge.isPending} onClick={() => form.submit()}>Merge</Button></>}>
      <Form form={form} layout="vertical" onFinish={(v) => merge.mutate(v)}>
        <Form.Item name="targetId" label="Keep (target)" rules={[{ required: true, message: 'Pick the surviving territory' }]}>
          <Select showSearch optionFilterProp="label" placeholder="Territory to keep"
            onChange={() => form.setFieldsValue({ sourceIds: [] })}
            options={active.map(opt)} />
        </Form.Item>
        <Form.Item name="sourceIds" label="Merge in (sources)" rules={[{ required: true, message: 'Pick at least one source' }]}
          extra={targetLevel ? <>Same level only: <Tag tone={LEVEL_COLOR[targetLevel]}>{targetLevel}</Tag>. Sources must have no sub-territories.</> : 'Pick the target first.'}>
          <Select mode="multiple" allowClear showSearch optionFilterProp="label" placeholder="Territories to fold in" disabled={!targetId}
            options={active.filter((t: any) => String(t.id) !== String(targetId) && t.level === targetLevel).map(opt)} />
        </Form.Item>
        <Alert type="warning" title="Sources are deactivated after their customers + Sales Person bindings move to the target. This can't be auto-undone." />
      </Form>
    </Modal>
  );
}
